import type { CSSProperties } from 'react';

/**
 * Themed content-surface keys a block can sit on. Each maps to a
 * {@code --color-surface-*} design token so the panel follows the active
 * theme (white in light mode, the theme's dark surface in dark mode)
 * instead of hard-coding a colour. {@code 'none'} (the default
 * everywhere) keeps the historical transparent, full-bleed look.
 */
export type SurfaceToken = 'none' | 'card' | 'elevated' | 'muted' | 'page' | 'sunken';

// Each key resolves through a fallback CHAIN of theme surface tokens
// before any literal, so the background always lands on a token that
// carries the theme's `$dark` variant — i.e. it flips correctly in dark
// mode and follows whatever palette the tenant's theme defines.
//
// The chain matters because themes disagree on surface naming: most use
// page/card/elevated/muted/sunken, but a few (akademi-net, marketplace)
// use base/raised/sunken. A single `var(--color-surface-card, #fff)`
// would miss those and fall to a hard white that stays white in dark
// mode. Chaining card → raised → base means we still land on a themed,
// dark-aware token there. The trailing literal is a genuine last resort
// (no current theme reaches it) and is only correct for light mode.
const SURFACE_VARS: Record<Exclude<SurfaceToken, 'none'>, string> = {
  card: 'var(--color-surface-card, var(--color-surface-raised, var(--color-surface-elevated, var(--color-surface-base, #ffffff))))',
  elevated:
    'var(--color-surface-elevated, var(--color-surface-raised, var(--color-surface-card, var(--color-surface-base, #ffffff))))',
  muted:
    'var(--color-surface-muted, var(--color-surface-subtle, var(--color-surface-sunken, #f1f5f9)))',
  page: 'var(--color-surface-page, var(--color-surface-base, #f8fafc))',
  sunken:
    'var(--color-surface-sunken, var(--color-surface-muted, var(--color-surface-subtle, #f1f5f9)))',
};

/**
 * Resolve a surface key to a CSS background value, or {@code undefined}
 * for {@code 'none'} / an unknown key (caller keeps its transparent
 * default). Tolerant of raw prop values (unknown string → transparent).
 */
export function surfaceBackground(surface?: string): string | undefined {
  if (!surface || surface === 'none') return undefined;
  return SURFACE_VARS[surface as Exclude<SurfaceToken, 'none'>];
}

/**
 * Bridge the block-facing {@code surface} colour onto the theme text
 * colour so body copy stays legible on the chosen surface in both
 * light and dark. Returns {@code {}} when there is no surface, so
 * callers can spread it unconditionally.
 */
export function surfaceStyle(surface?: string): CSSProperties {
  const background = surfaceBackground(surface);
  if (!background) return {};
  return { background, color: 'var(--color-text-primary, inherit)' };
}
